// src/pages/Projects.jsx
import { useMemo, useState, useEffect } from "react";
import { SITE } from "../data/site";
import { Link } from "react-router-dom";
import { setPageSeo } from "../utils/seo";

/* ================= PROJECT DATA (10 PROJECTS) ================= */
const PROJECTS = [
  {
    title: "Retractable Awning Installation",
    tag: "Awning",
    note: "Residential & Commercial Buildings",
    img: "/images/Retractable-Awning.png",
  },
  {
    title: "Motorised Awning System",
    tag: "Awning",
    note: "Cafe & Outdoor Seating Area",
    img: "/images/Motorised-Awning-System.png",
  },
  {
    title: "Polycarbonate Roofing Installation",
    tag: "Roofing",
    note: "Car Porch & Patio Area",
    img: "/images/Polycarbonate-Roofing-Installation.png",
  },
  {
    title: "Glass Canopy Shelter",
    tag: "Shelter",
    note: "Walkway & Main Entrance",
    img: "/images/Glass-Canopy-Shelter-.png",
  },
  {
    title: "Outdoor Zip Track System",
    tag: "Zip Track",
    note: "Balcony & Patio Enclosure",
    img: "/images/Outdoor-Zip-Track-System.png",
  },
  {
    title: "ACP Roofing with Metal Structure",
    tag: "ACP Roof",
    note: "Outdoor Extension Area",
    img: "/images/ACP-Roofing-with-Metal-Structure.png",
  },
  {
    title: "Aluminium Louvers Installation",
    tag: "Roofing",
    note: "Modern Residential House",
    img: "/images/Aluminium-Louvers-Installation.png",
  },
  {
    title: "Glass Roof Canopy",
    tag: "Shelter",
    note: "Commercial Building Entrance",
    img: "/images/Glass-Roof-Canopy.png",
  },
  {
    title: "Wooden Decking & Pergola",
    tag: "Renovation",
    note: "Garden & Backyard Area",
    img: "/images/Wooden-Decking-Pergola.png",
  },
  {
    title: "Custom Metal Awning",
    tag: "Awning",
    note: "Industrial & Factory Area",
    img: "/images/Custom-Metal-Awning.png",
  },
];

export default function Projects() {
  const PAGE_URL = `${SITE.domain}/projects`;

  const SEO_TITLE =
    "Completed Projects | Awning, Roofing, Shelter & Zip Track in Singapore";
  const SEO_DESC =
    "Explore completed awning, roofing, shelter, zip track and outdoor renovation projects by Din Lee Engineering in Singapore.";

  /* ================= FILTER ================= */
  const tags = useMemo(
    () => ["All", ...Array.from(new Set(PROJECTS.map((p) => p.tag)))],
    []
  );
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return PROJECTS;
    return PROJECTS.filter((p) => p.tag === active);
  }, [active]);

  /* ================= SEO META ================= */
  useEffect(() => {
    setPageSeo({
      title: SEO_TITLE,
      description: SEO_DESC,
      canonical: PAGE_URL,
    });
  }, [PAGE_URL]);

  return (
    <>
      {/* ================= HERO / H1 ================= */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">
            Completed Awning, Roofing & Shelter Projects in Singapore
          </h1>

          <p className="mt-4 max-w-3xl text-slate-600">
            Din Lee Engineering has successfully completed numerous awning,
            roofing, glass canopy, zip track and outdoor renovation projects
            across Singapore. Below are selected projects showcasing our
            workmanship, materials and installation quality.
          </p>

          {/* Filter */}
          <div className="mt-6 flex flex-wrap gap-2">
            {tags.map((t) => (
              <button
                key={t}
                onClick={() => setActive(t)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                  active === t
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROJECT GRID ================= */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <h2 className="sr-only">
          Awning, Roofing, Shelter and Zip Track Project Gallery
        </h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <div
              key={p.title}
              className="rounded-3xl border bg-white overflow-hidden shadow-sm hover:shadow-lg transition"
            >
              {/* Image (FIXED HEIGHT ISSUE) */}
              <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                <img
                  src={p.img}
                  alt={`${p.title} project in Singapore`}
                  className="h-full w-full object-cover hover:scale-105 transition duration-500"
                  loading="lazy"
                />
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="text-xs font-semibold text-blue-600 uppercase">
                  {p.tag}
                </div>
                <h3 className="mt-2 text-lg font-extrabold text-slate-900">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  {p.note}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= SEO CONTENT ================= */}
      <section className="bg-slate-50 border-t">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <h2 className="text-2xl font-extrabold text-slate-900">
            Trusted Awning & Roofing Contractor in Singapore
          </h2>
          <p className="mt-4 max-w-4xl text-slate-600">
            We specialise in custom awnings, polycarbonate roofing, glass
            canopy shelters, outdoor zip track systems and metal works for
            residential, commercial and industrial properties. From site
            measurement to fabrication and installation, every project is
            handled by our experienced in-house team.
          </p>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 py-12 text-white grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h2 className="text-2xl font-extrabold">
              Planning a Similar Project?
            </h2>
            <p className="text-white/80 mt-2">
              Contact us with your site location, size and requirement. We will
              recommend the best awning or roofing solution for you.
            </p>
          </div>
          <div className="md:text-right flex md:justify-end gap-3 flex-wrap">
            <a
              href={`tel:${SITE.phone}`}
              className="inline-block px-6 py-3 rounded-xl bg-white text-slate-900 font-bold hover:bg-slate-100 transition"
            >
              Call {SITE.phone}
            </a>
            <Link
              to="/contact"
              className="inline-block px-6 py-3 rounded-xl border border-white/30 text-white font-bold hover:bg-white/10 transition"
            >
              Contact Form
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
